import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  Stack,
  TextField,
  Button,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../utils/axios';
import { formatKstDate, formatKstDateTime } from '../utils/formatUtils';

const Section = ({ title, children }) => (
  <Paper sx={{ p: 3, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const MyAccount = () => {
  const navigate = useNavigate();
  const { user, fetchUser, logout } = useAuth();
  const [profile, setProfile] = useState({ username: '', phoneNumber: '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);

  useEffect(() => {
    if (user) setProfile({ username: user.username || '', phoneNumber: user.phoneNumber || '' });
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await apiClient.put('/auth/profile', profile);
      setProfileMsg({ severity: 'success', text: res.data.message });
      fetchUser();
    } catch (err) {
      setProfileMsg({ severity: 'error', text: getErrorMessage(err) });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirm) {
      setPwMsg({ severity: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }
    try {
      const res = await apiClient.put('/auth/change-password', {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      setPwMsg({ severity: 'success', text: res.data.message });
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setPwMsg({ severity: 'error', text: getErrorMessage(err) });
    }
  };

  if (!user) return null;
  const premium = user.subscription === 'Y';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon fontSize="small" color="primary" /> PlanGo 계정
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {user.isAdmin && <Button onClick={() => navigate('/admin')}>관리자 콘솔</Button>}
            <Button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              로그아웃
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Section title="구독">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip color={premium ? 'primary' : 'default'} label={user.subscriptionPlanName || '무료'} />
            {user.subscriptionCycle && <Chip variant="outlined" label={user.subscriptionCycle} />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {premium
              ? `이용 기간: ${formatKstDate(user.subscriptionStartedAt)} ~ ${formatKstDate(user.subscriptionExpiresAt)}`
              : '무료 회원입니다. 구독 신청·변경은 고객센터로 문의해주세요.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            관심종목 {user.maxWatchlist ?? '무제한'} · 자동매매 종목 {user.maxAutoTrading ?? '무제한'}
          </Typography>
        </Section>

        <Section title="개인정보">
          <Box component="form" onSubmit={saveProfile}>
            <Stack spacing={2}>
              {profileMsg && <Alert severity={profileMsg.severity}>{profileMsg.text}</Alert>}
              <TextField label="이메일" value={user.email} disabled />
              <TextField
                label="사용자명"
                value={profile.username}
                onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))}
              />
              <TextField
                label="휴대폰 번호"
                value={profile.phoneNumber}
                onChange={(e) => setProfile((p) => ({ ...p, phoneNumber: e.target.value }))}
                placeholder="01012345678"
              />
              <Typography variant="caption" color="text.secondary">
                가입일 {formatKstDateTime(user.createdAt)} · 최근 로그인 {formatKstDateTime(user.lastLoginAt)}
              </Typography>
              <Box>
                <Button type="submit" variant="contained">
                  저장
                </Button>
              </Box>
            </Stack>
          </Box>
        </Section>

        <Section title="비밀번호 변경">
          <Box component="form" onSubmit={changePassword}>
            <Stack spacing={2}>
              {pwMsg && <Alert severity={pwMsg.severity}>{pwMsg.text}</Alert>}
              <TextField
                type="password"
                label="현재 비밀번호"
                value={pw.currentPassword}
                onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))}
                autoComplete="current-password"
              />
              <TextField
                type="password"
                label="새 비밀번호 (6자 이상)"
                value={pw.newPassword}
                onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
                autoComplete="new-password"
              />
              <TextField
                type="password"
                label="새 비밀번호 확인"
                value={pw.confirm}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
              />
              <Box>
                <Button type="submit" variant="contained">
                  변경
                </Button>
              </Box>
            </Stack>
          </Box>
        </Section>
      </Container>
    </Box>
  );
};

export default MyAccount;
